import { Entity } from '@backstage/catalog-model';
import { ConfigApi as Config } from '@backstage/core-plugin-api';

/**
 * Per-environment Config / Secrets / Metrics / Logs URLs for an entity.
 *
 * Apps created by DevOps/app-bootstrap run in staging and/or production;
 * `sparrow.io/environments` (written by the bootstrap into catalog-info.yaml)
 * says which. Entities without it are treated as staging-only, as before.
 *
 * Everything that differs per BU and environment lives in frontend config
 * under the BU's named block (named fields only: Backstage's frontend
 * visibility filter drops values under index-signature maps):
 *
 *   sparrow:
 *     vaultBaseUrl: https://vault.internal.sparrowapps.com
 *     platforms:
 *       configRepoOwner: sparrow-platforms
 *       vaultMount: Platforms
 *       staging:
 *         configRepoName: platforms-staging-config
 *         configBranch: master
 *         vaultEnvPath: staging           # Vault path segment (casing differs per BU)
 *         cluster: platforms-staging-cluster   # `cluster` label in Prometheus/Loki
 *         metricsDatasource: afufpcagjdbswc    # Prometheus UID on the metrics Grafana
 *         logsDatasource: bg0sifmldwttsf       # Loki UID on that env's logs Grafana
 *         logsCluster: platforms-staging-cluster   # optional `cluster` label in Loki
 *       production:
 *         ...
 *
 * Logs: app-bootstrap apps (both environments) use the one Grafana at
 * `grafana.appLogs.baseUrl`, with the dashboard when
 * `grafana.appLogs.logsDashboardPath` is set, otherwise Grafana Explore; the
 * environment picks the Loki datasource and cluster. Entities without
 * `sparrow.io/environments` keep `grafana.baseUrl` + `grafana.logsDashboardPath`
 * (their legacy datasources live there).
 * Metrics for both use the pod-metrics dashboard on `grafana.metricsBaseUrl`.
 */

export const ENVIRONMENTS_ANNOTATION = 'sparrow.io/environments';
export const KNOWN_ENVIRONMENTS = ['staging', 'production'] as const;
export type AppEnvironment = (typeof KNOWN_ENVIRONMENTS)[number];

export const ENV_LABEL: Record<AppEnvironment, string> = {
  staging: 'Staging',
  production: 'Production',
};

const inferBuFromGithubOrg = (org: string | undefined): string | undefined => {
  if (!org) return undefined;
  if (org === 'sparrow-platforms') return 'platforms';
  return org;
};

/** BU: `sparrow.io/bu` > entity namespace (unless `default`) > GitHub org. */
export function resolveBu(entity: Entity): string | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const orgFromSlug = annotations['github.com/project-slug']?.split('/')[0];
  return (
    annotations['sparrow.io/bu'] ??
    (entity.metadata.namespace && entity.metadata.namespace !== 'default'
      ? entity.metadata.namespace
      : undefined) ??
    inferBuFromGithubOrg(orgFromSlug)
  );
}

/** k8s namespace (bootstrap convention: namespace == service name). */
export function resolveNamespace(entity: Entity): string {
  const annotations = entity.metadata.annotations ?? {};
  return (
    annotations['grafana.com/namespace'] ??
    annotations['backstage.io/kubernetes-namespace'] ??
    entity.metadata.name
  );
}

/** Environments from `sparrow.io/environments`, staging first; staging-only when absent. */
export function entityEnvironments(entity: Entity): AppEnvironment[] {
  const raw = entity.metadata.annotations?.[ENVIRONMENTS_ANNOTATION] ?? '';
  const listed = raw.split(',').map(e => e.trim().toLowerCase());
  const envs = KNOWN_ENVIRONMENTS.filter(e => listed.includes(e));
  return envs.length ? envs : ['staging'];
}

export type EnvLinks = {
  env: AppEnvironment;
  config?: { url: string; path: string };
  secrets?: { url: string; path: string };
  metrics?: string;
  /** Logs page for a new tab. */
  logs?: string;
  /** Same logs view, chrome-less, for the Logs tab iframe. */
  logsEmbed?: string;
  /** Why logs can't be shown, when they can't. */
  logsMissing?: string;
};

const trimSlash = (s: string) => s.replace(/\/+$/, '');
const trimLead = (s: string) => s.replace(/^\/+/, '');

function withQuery(base: string, path: string, params: Record<string, string | undefined>) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  return `${trimSlash(base)}/${trimLead(path)}?${qs.toString()}`;
}

/**
 * The per-env block for a BU, falling back (staging only) to the BU's
 * top-level keys, which are all that entities without `sparrow.io/environments`
 * use (their links stay exactly as before).
 */
function envBlock(config: Config, bu: string, env: AppEnvironment, perEnv: boolean) {
  const buConfig = config.getOptionalConfig(`sparrow.${bu}`);
  const block = perEnv ? buConfig?.getOptionalConfig(env) : undefined;
  const get = (key: string) =>
    block?.getOptionalString(key) ??
    (env === 'staging' ? buConfig?.getOptionalString(key) : undefined);
  return { buConfig, get };
}

export function envLinks(
  config: Config,
  entity: Entity,
  env: AppEnvironment,
): EnvLinks {
  const name = entity.metadata.name;
  const namespace = resolveNamespace(entity);
  const annotations = entity.metadata.annotations ?? {};
  const bu = resolveBu(entity);
  const out: EnvLinks = { env };
  if (!bu) return out;
  // Per-env blocks apply only to app-bootstrap apps (they carry
  // sparrow.io/environments); other entities keep the BU's top-level keys.
  const perEnv = Boolean(annotations[ENVIRONMENTS_ANNOTATION]);
  const { buConfig, get } = envBlock(config, bu, env, perEnv);
  // BU-wide keys (repo owner, Vault mount) may sit in the env block too, so
  // a BU can be set up for app-bootstrap apps only (no top-level keys).
  const shared = (key: string) =>
    get(key) ?? (perEnv ? buConfig?.getOptionalString(key) : undefined);
  // Pre-per-env staging keys (grafana.<map>.<bu>, then grafana.<default>),
  // for entities without sparrow.io/environments only: for app-bootstrap
  // apps a missing datasource is reported rather than guessed.
  const legacy = (map: string, fallback: string) =>
    config.getOptionalConfig(`grafana.${map}`)?.getOptionalString(bu) ??
    config.getOptionalString(`grafana.${fallback}`);

  // Config: <owner>/<repo>/tree/<branch>/<appsPath>/<name>
  const owner = shared('configRepoOwner');
  const repo = get('configRepoName');
  if (owner && repo) {
    const branch = get('configBranch') ?? 'master';
    const appsPath = get('configAppsPath') ?? 'apps';
    out.config = {
      url: `https://github.com/${owner}/${repo}/tree/${branch}/${appsPath}/${name}`,
      path: `${owner}/${repo} → ${appsPath}/${name}`,
    };
  }

  // Secrets: <vault>/ui/vault/secrets-engines/<mount>/kv/list/<envPath>/services/<name>/
  const vaultBaseUrl = config.getOptionalString('sparrow.vaultBaseUrl');
  const vaultMount = shared('vaultMount');
  if (vaultBaseUrl && vaultMount) {
    const envPath = get('vaultEnvPath') ?? env;
    out.secrets = {
      url: `${trimSlash(vaultBaseUrl)}/ui/vault/secrets-engines/${vaultMount}/kv/list/${envPath}/services/${name}/`,
      path: `Vault → ${vaultMount} → ${envPath}/services/${name}`,
    };
  }

  // Metrics: pod-metrics dashboard; its variables chain off the datasource.
  const metricsBase = config.getOptionalString('grafana.metricsBaseUrl');
  const metricsPath = config.getOptionalString('grafana.metricsDashboardPath');
  if (metricsBase && metricsPath) {
    out.metrics = withQuery(metricsBase, metricsPath, {
      'var-datasource':
        get('metricsDatasource') ??
        (env === 'staging' && !perEnv ? legacy('metricsDatasourceByOrg', 'defaultMetricsDatasource') : undefined),
      'var-cluster': get('cluster'),
      'var-namespace': namespace,
      'var-deployment': annotations['grafana.com/deployment'] ?? `${namespace}-application`,
      'var-eventexporter_loki_ds':
        env === 'staging' ? config.getOptionalString('grafana.metricsEventsLokiDs') : undefined,
    });
  }

  // Logs
  const logsDs =
    annotations['grafana.com/logs-datasource'] ??
    get('logsDatasource') ??
    (env === 'staging' && !perEnv ? legacy('logsDatasourceByOrg', 'defaultLogsDatasource') : undefined);
  // grafana.appLogs (empress) is the logs Grafana for every entity once configured: per-env
  // entities in both envs, older entities for staging. Without it, older entities fall back
  // to the old staging Grafana (grafana.baseUrl + its app-logs dashboard).
  const appLogs = config.getOptionalConfig('grafana.appLogs');
  const useAppLogs = Boolean(appLogs) && (perEnv || env === 'staging');
  const logsBase = useAppLogs
    ? appLogs?.getOptionalString('baseUrl')
    : perEnv
      ? undefined
      : env === 'staging'
        ? config.getOptionalString('grafana.baseUrl')
        : undefined;
  const logsDashboard = useAppLogs
    ? appLogs?.getOptionalString('logsDashboardPath')
    : config.getOptionalString('grafana.logsDashboardPath') ??
      config.getOptionalString('grafana.appDashboardPath') ??
      'd/app-logs/app-logs';
  // A tenant can hold several clusters' logs; pin the app's one when known.
  const logsCluster = get('logsCluster');

  if (!logsBase) {
    out.logsMissing = `No ${env} logs Grafana is configured in Launchpad.`;
  } else if (!logsDs) {
    out.logsMissing = `No ${env} Loki datasource is configured for ${bu}.`;
  } else if (logsDashboard) {
    const params = {
      'var-namespace': namespace,
      'var-logs_ds': logsDs,
      'var-cluster': logsCluster,
    };
    out.logs = withQuery(logsBase, logsDashboard, params);
    out.logsEmbed = withQuery(logsBase, logsDashboard, { ...params, kiosk: 'tv' });
  } else {
    // No app-logs dashboard on this Grafana: Explore with the namespace query.
    const panes = JSON.stringify({
      a: {
        datasource: logsDs,
        queries: [
          {
            refId: 'A',
            expr: logsCluster
              ? `{namespace="${namespace}", cluster="${logsCluster}"}`
              : `{namespace="${namespace}"}`,
            queryType: 'range',
            datasource: { type: 'loki', uid: logsDs },
          },
        ],
        range: { from: 'now-1h', to: 'now' },
      },
    });
    const params = { schemaVersion: '1', orgId: '1', panes };
    out.logs = withQuery(logsBase, 'explore', params);
    out.logsEmbed = withQuery(logsBase, 'explore', { ...params, kiosk: '1' });
  }
  return out;
}
